import { apiService } from './api';
import { ContentBlockType, ContentStatus, CreateUpdateContentBlockDto } from './contentService';
import { ContentCategoryDto } from './contentCategoryService';
import {
  normalizeContentStatus,
  normalizeLibraryBookId,
  normalizeLibraryBookType,
  normalizeLibraryDifficulty,
  normalizePlanIds
} from '../utils/libraryBookDisplay';
import { backfillLegacyBlockOriginalAudio } from '../utils/blockVoiceStyleSave';

export enum LibraryBookDifficulty {
  Simple = 0,
  Intermediate = 1,
  Advanced = 2
}

export const LIBRARY_DIFFICULTY_LABELS: Record<LibraryBookDifficulty, string> = {
  [LibraryBookDifficulty.Simple]: 'Simple',
  [LibraryBookDifficulty.Intermediate]: 'Intermediate',
  [LibraryBookDifficulty.Advanced]: 'Advanced'
};

export enum LibraryBookType {
  Classic = 0,
  New = 1
}

export const LIBRARY_BOOK_TYPE_LABELS: Record<LibraryBookType, string> = {
  [LibraryBookType.Classic]: 'Classic',
  [LibraryBookType.New]: 'New'
};

export interface LibraryBookBlockDto {
  id: number;
  libraryBookId: number;
  type: ContentBlockType;
  displayOrder: number;
  title?: string;
  titleFa?: string;
  textContent?: string;
  audioUrl?: string;
  audioFileName?: string;
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
  imageUrl?: string;
  transcript?: string;
  durationSeconds?: number;
  tips?: string;
  stickyAudio?: string;
  textFormatting?: string;
  isDownloadable?: boolean;
  audioSyncData?: string;
  vocabularyItems?: import('./contentService').ContentVocabularyItemDto[];
}

export interface LibraryBookDto {
  id: number;
  title: string;
  author?: string;
  farsiTitle?: string;
  farsiAuthor?: string;
  description?: string;
  descriptionFa?: string;
  coverImageUrl?: string;
  externalLinkUrl?: string;
  difficulty: LibraryBookDifficulty;
  bookType: LibraryBookType;
  displayOrder: number;
  status: ContentStatus;
  blocks: LibraryBookBlockDto[];
  subscriptionPlanIds: number[];
  categoryIds: number[];
  categoryNames?: string[];
  viewCount?: number;
}

export interface LibraryBookViewStatDto {
  bookId: number;
  title: string;
  viewCount: number;
}

export interface LibraryBookRatingStatDto {
  bookId: number;
  title: string;
  averageRating: number;
  ratingCount: number;
}

export interface LibraryStatisticsDto {
  totalBooks: number;
  publishedBooks: number;
  draftBooks: number;
  totalViews: number;
  booksWithRatings: number;
  topBooksByViews: LibraryBookViewStatDto[];
  topBooksByRating: LibraryBookRatingStatDto[];
  booksBySubscriptionTier: Record<string, number>;
  booksByDifficulty: Record<string, number>;
  booksByType: Record<string, number>;
}

export interface CreateUpdateLibraryBookDto {
  title: string;
  author?: string;
  farsiTitle?: string;
  farsiAuthor?: string;
  description?: string;
  descriptionFa?: string;
  coverImageUrl?: string;
  externalLinkUrl?: string;
  difficulty: LibraryBookDifficulty;
  bookType: LibraryBookType;
  displayOrder: number;
  status: ContentStatus;
  blocks: CreateUpdateContentBlockDto[];
  subscriptionPlanIds: number[];
  categoryIds: number[];
}

export interface LibraryBookSummaryDto {
  id: number;
  title: string;
  author?: string;
  farsiTitle?: string;
  farsiAuthor?: string;
  description?: string;
  descriptionFa?: string;
  coverImageUrl?: string;
  difficulty: LibraryBookDifficulty;
  bookType: LibraryBookType;
  displayOrder: number;
  subscriptionPlanIds: number[];
  categoryIds: number[];
  categoryNames?: string[];
  isAccessible: boolean;
  isFavorite?: boolean;
  isFinished?: boolean;
  hasStarted?: boolean;
  viewCount?: number;
}

export interface LibraryBookDetailDto {
  id: number;
  title: string;
  author?: string;
  farsiTitle?: string;
  farsiAuthor?: string;
  description?: string;
  descriptionFa?: string;
  coverImageUrl?: string;
  externalLinkUrl?: string;
  difficulty: LibraryBookDifficulty;
  bookType: LibraryBookType;
  blocks: LibraryBookBlockDto[];
  isAccessible: boolean;
  accessMessage?: string;
  requiredSubscriptionPlans?: { id: number; name: string; description?: string }[];
  categoryIds: number[];
  categoryNames?: string[];
  isFavorite?: boolean;
  isFinished?: boolean;
  hasStarted?: boolean;
  viewCount?: number;
}

export interface LibraryBookProgressDto {
  libraryBookId: number;
  isFinished: boolean;
  firstOpenedAt?: string;
  lastOpenedAt?: string;
  finishedAt?: string;
  timeSpentSeconds?: number;
}

export interface UpdateLibraryBookProgressDto {
  isFinished?: boolean;
  recordOpen?: boolean;
  addTimeSpentSeconds?: number;
}

export interface LearnerLibraryBookBreakdownDto {
  label: string;
  reviewed: number;
  total: number;
}

export interface LearnerLibraryBookStatsDto {
  totalAccessible: number;
  reviewedCount: number;
  byDifficulty: LearnerLibraryBookBreakdownDto[];
  byType: LearnerLibraryBookBreakdownDto[];
  byCategory: LearnerLibraryBookBreakdownDto[];
}

export interface FavoriteLibraryBookItemDto {
  id: number;
  libraryBookId: number;
  title: string;
  farsiTitle?: string;
  author?: string;
  farsiAuthor?: string;
  description?: string;
  descriptionFa?: string;
  coverImageUrl?: string;
  difficulty: LibraryBookDifficulty;
  createdAt: string;
  /** False when the book was withdrawn (trashed) or unpublished. */
  isAccessible?: boolean;
}

function normalizeBookSummary<
  T extends { id?: unknown; difficulty?: unknown; bookType?: unknown; subscriptionPlanIds?: unknown }
>(
  raw: T
): T & { id: number; difficulty: LibraryBookDifficulty; bookType: LibraryBookType; subscriptionPlanIds: number[] } {
  const rec = raw as Record<string, unknown>;
  const id = normalizeLibraryBookId(raw.id ?? rec.Id) ?? 0;
  return {
    ...raw,
    id,
    difficulty: normalizeLibraryDifficulty(raw.difficulty ?? rec.Difficulty),
    bookType: normalizeLibraryBookType((raw as { bookType?: unknown }).bookType ?? rec.BookType),
    subscriptionPlanIds: normalizePlanIds(raw.subscriptionPlanIds ?? rec.SubscriptionPlanIds),
    categoryIds: normalizePlanIds((raw as { categoryIds?: unknown }).categoryIds ?? rec.CategoryIds),
    categoryNames: (raw as { categoryNames?: string[] }).categoryNames ??
      (Array.isArray(rec.CategoryNames) ? (rec.CategoryNames as string[]) : undefined),
    viewCount: Number(raw.viewCount ?? rec.ViewCount ?? 0) || 0
  };
}

function normalizeBookAdmin(raw: LibraryBookDto): LibraryBookDto {
  const summary = normalizeBookSummary(raw);
  const rec = raw as unknown as Record<string, unknown>;
  return {
    ...raw,
    ...summary,
    status: normalizeContentStatus(raw.status ?? rec.Status),
    blocks: raw.blocks ?? []
  };
}

class LibraryService {
  async getAllBooksAdmin(): Promise<LibraryBookDto[]> {
    const list = await apiService.get<LibraryBookDto[]>('/api/Library/admin/books', { cache: false });
    return list.map((b) => normalizeBookAdmin(b));
  }

  async getLibraryStatisticsAdmin(): Promise<LibraryStatisticsDto> {
    const raw = await apiService.get<Record<string, unknown>>('/api/Library/admin/statistics', { cache: false });
    const rec = raw as Record<string, unknown>;
    const mapView = (row: Record<string, unknown>) => ({
      bookId: Number(row.bookId ?? row.BookId ?? 0),
      title: String(row.title ?? row.Title ?? ''),
      viewCount: Number(row.viewCount ?? row.ViewCount ?? 0)
    });
    const mapRating = (row: Record<string, unknown>) => ({
      bookId: Number(row.bookId ?? row.BookId ?? 0),
      title: String(row.title ?? row.Title ?? ''),
      averageRating: Number(row.averageRating ?? row.AverageRating ?? 0),
      ratingCount: Number(row.ratingCount ?? row.RatingCount ?? 0)
    });
    const mapDict = (d: unknown): Record<string, number> => {
      if (!d || typeof d !== 'object') return {};
      return Object.fromEntries(
        Object.entries(d as Record<string, unknown>).map(([k, v]) => [k, Number(v) || 0])
      );
    };
    const topViews = (rec.topBooksByViews ?? rec.TopBooksByViews) as unknown;
    const topRating = (rec.topBooksByRating ?? rec.TopBooksByRating) as unknown;
    return {
      totalBooks: Number(rec.totalBooks ?? rec.TotalBooks ?? 0),
      publishedBooks: Number(rec.publishedBooks ?? rec.PublishedBooks ?? 0),
      draftBooks: Number(rec.draftBooks ?? rec.DraftBooks ?? 0),
      totalViews: Number(rec.totalViews ?? rec.TotalViews ?? 0),
      booksWithRatings: Number(rec.booksWithRatings ?? rec.BooksWithRatings ?? 0),
      topBooksByViews: Array.isArray(topViews) ? topViews.map((r) => mapView(r as Record<string, unknown>)) : [],
      topBooksByRating: Array.isArray(topRating) ? topRating.map((r) => mapRating(r as Record<string, unknown>)) : [],
      booksBySubscriptionTier: mapDict(rec.booksBySubscriptionTier ?? rec.BooksBySubscriptionTier),
      booksByDifficulty: mapDict(rec.booksByDifficulty ?? rec.BooksByDifficulty),
      booksByType: mapDict(rec.booksByType ?? rec.BooksByType)
    };
  }

  async getBookAdmin(bookId: number): Promise<LibraryBookDto> {
    const book = await apiService.get<LibraryBookDto>(`/api/Library/admin/books/${bookId}`, { cache: false });
    return normalizeBookAdmin(book);
  }

  async createBook(dto: CreateUpdateLibraryBookDto): Promise<LibraryBookDto> {
    return apiService.post<LibraryBookDto>('/api/Library/admin/books', dto);
  }

  async updateBook(bookId: number, dto: CreateUpdateLibraryBookDto): Promise<LibraryBookDto> {
    return apiService.put<LibraryBookDto>(`/api/Library/admin/books/${bookId}`, dto);
  }

  async deleteBook(bookId: number): Promise<void> {
    await apiService.delete(`/api/Library/admin/books/${bookId}`);
  }

  async publishBook(bookId: number): Promise<void> {
    await apiService.post(`/api/Library/admin/books/${bookId}/publish`, {});
  }

  async unpublishBook(bookId: number): Promise<void> {
    await apiService.post(`/api/Library/admin/books/${bookId}/unpublish`, {});
  }

  async uploadBookCover(bookId: number, file: File): Promise<{ url: string }> {
    const result = await apiService.uploadFile<{ url?: string; Url?: string }>(
      `/api/Library/admin/books/${bookId}/upload-cover`,
      file
    );
    const url = result.url ?? result.Url;
    if (!url) throw new Error('Cover upload failed');
    return { url };
  }

  async getPublishedBooks(
    categoryId?: number | null,
    difficulty?: LibraryBookDifficulty | null,
    bookType?: LibraryBookType | null,
    search?: string | null,
    reviewed?: boolean | null
  ): Promise<LibraryBookSummaryDto[]> {
    const params = new URLSearchParams();
    if (categoryId != null) params.set('categoryId', String(categoryId));
    if (difficulty != null) params.set('difficulty', String(difficulty));
    if (bookType != null) params.set('bookType', String(bookType));
    const term = search?.trim();
    if (term) params.set('search', term);
    if (reviewed != null) params.set('reviewed', String(reviewed));
    const q = params.toString();
    const list = await apiService.get<LibraryBookSummaryDto[]>(`/api/Library/books${q ? `?${q}` : ''}`, {
      cache: false
    });
    return list.map((b) => normalizeBookSummary(b) as LibraryBookSummaryDto);
  }

  async getBookDetail(bookId: number): Promise<LibraryBookDetailDto> {
    const id = normalizeLibraryBookId(bookId);
    if (id == null) throw new Error('Invalid book id');
    const book = await apiService.get<LibraryBookDetailDto>(`/api/Library/books/${id}`, { cache: false });
    const rec = book as unknown as Record<string, unknown>;
    return {
      ...book,
      id,
      difficulty: normalizeLibraryDifficulty(book.difficulty ?? rec.Difficulty),
      bookType: normalizeLibraryBookType(book.bookType ?? rec.BookType),
      viewCount: Number(book.viewCount ?? rec.ViewCount ?? 0) || 0
    };
  }

  async getPublishedCategories(): Promise<ContentCategoryDto[]> {
    return apiService.get<ContentCategoryDto[]>('/api/Library/categories', { cache: false });
  }

  async getBookProgress(bookId: number): Promise<LibraryBookProgressDto> {
    const id = normalizeLibraryBookId(bookId);
    if (id == null) throw new Error('Invalid book id');
    return apiService.get<LibraryBookProgressDto>(`/api/Library/books/${id}/progress`, { cache: false });
  }

  async updateBookProgress(bookId: number, dto: UpdateLibraryBookProgressDto): Promise<LibraryBookProgressDto> {
    const id = normalizeLibraryBookId(bookId);
    if (id == null) throw new Error('Invalid book id');
    return apiService.put<LibraryBookProgressDto>(`/api/Library/books/${id}/progress`, dto);
  }

  async getFavoriteBooks(): Promise<FavoriteLibraryBookItemDto[]> {
    return apiService.get<FavoriteLibraryBookItemDto[]>('/api/Library/favorites', { cache: false });
  }

  async addBookFavorite(bookId: number): Promise<void> {
    const id = normalizeLibraryBookId(bookId);
    if (id == null) throw new Error('Invalid book id');
    await apiService.post(`/api/Library/favorites/${id}`, {});
  }

  async removeBookFavorite(bookId: number): Promise<void> {
    const id = normalizeLibraryBookId(bookId);
    if (id == null) throw new Error('Invalid book id');
    await apiService.delete(`/api/Library/favorites/${id}`);
  }
}

export const libraryService = new LibraryService();

/** Map library blocks to lesson content block shape for LessonDetailView reuse. */
export function libraryBlocksToLessonBlocks(blocks: LibraryBookBlockDto[]): CreateUpdateContentBlockDto[] {
  return (blocks ?? []).map((block) =>
    backfillLegacyBlockOriginalAudio({
      id: block.id,
      type: block.type,
      displayOrder: block.displayOrder,
      title: block.title,
      titleFa: block.titleFa,
      textContent: block.textContent,
      audioUrl: block.audioUrl,
      audioFileName: block.audioFileName,
      originalAudioUrl: block.originalAudioUrl,
      voiceStylePresetId: block.voiceStylePresetId,
      imageUrl: block.imageUrl,
      transcript: block.transcript,
      durationSeconds: block.durationSeconds,
      tips: block.tips,
      stickyAudio: block.stickyAudio,
      textFormatting: block.textFormatting,
      isDownloadable: block.isDownloadable,
      audioSyncData: block.audioSyncData,
      categoryIds: [],
      vocabularyItems: block.vocabularyItems ?? []
    })
  );
}

export function bookToLessonShape(book: LibraryBookDto): import('./contentService').LessonDto {
  return {
    id: book.id,
    levelId: 0,
    title: book.title,
    description: book.description ?? '',
    displayOrder: book.displayOrder,
    status: book.status,
    createdAt: new Date().toISOString(),
    contentBlocks: book.blocks.map((b) => ({
      id: b.id,
      lessonId: 0,
      type: b.type,
      displayOrder: b.displayOrder,
      title: b.title,
      textContent: b.textContent,
      audioUrl: b.audioUrl,
      audioFileName: b.audioFileName,
      imageUrl: b.imageUrl,
      transcript: b.transcript,
      durationSeconds: b.durationSeconds,
      tips: b.tips,
      textFormatting: b.textFormatting,
      isDownloadable: b.isDownloadable ?? false,
      audioSyncData: b.audioSyncData,
      createdAt: new Date().toISOString(),
      categoryIds: [],
      vocabularyItems: b.vocabularyItems ?? []
    })),
    subscriptionPlanIds: book.subscriptionPlanIds,
    categoryIds: book.categoryIds,
    tagIds: [],
    visibleInStructuredPath: true,
    requiresQuizToPass: false,
    quizzes: [],
    exercises: []
  } as import('./contentService').LessonDto;
}
